// src/components/Phaser/index.tsx
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
} from "react";
import { GameEngine } from "./engine/GameEngine";
import * as functions from "./functions";
import { EventManager } from "./engine/events/EventManager";
import {
    CharacterConfig,
    ObjectConfig,
    PhaserConfig,
    TextObjectConfig,
} from "./types/config";
import {
    PhaserConfigProvider,
    usePhaserConfig,
} from "./contexts/PhaserConfigContext";
import ConfiguratorPanel from "./configurator/ConfiguratorPanel";
import { TSprite } from "../../models/objects/sprite";
import { TObstacle, TScene } from "../../models/objects/objects";
import { TSound } from "../../models/objects/media";
import { buildImageAssets, mergeSounds } from "./engine/AssetBuilder";
import { Button, Checkbox, Drawer, Popover, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";


export interface PhaserRef {
    getConfig: () => PhaserConfig;
    handleResetConfig: () => Promise<void>;
}

// On reçoit eventManager en props
interface PhaserViewProps {
    adminMode: boolean;
    projectMode: boolean;
    eventManager?: EventManager | null;
    config: PhaserConfig;
    isRunning: boolean;
    onWin: () => void;
    scenesConfig: TScene[];
    sprites: TSprite[];
    obstacles: TObstacle[];
    textsConfig: TextObjectConfig[];
    sounds: TSound[];
}

const PhaserComponent = forwardRef<PhaserRef, PhaserViewProps>(
    (
        {
            adminMode,
            projectMode,
            eventManager,
            config,
            isRunning,
            onWin,
            sprites,
            scenesConfig,
            obstacles,
            textsConfig,
            sounds,
        },
        ref
    ) => {
        const initialConfigRef = useRef<PhaserConfig>(config);
        const engineRef = useRef<GameEngine | null>(null);
        const phaserContainerRef = useRef<HTMLDivElement | null>(null);
        const [engineNonce, setEngineNonce] = useState(0);
        const [bootError, setBootError] = useState<string | null>(null);

        // ─────────────────────────────────────────────────────────────
        // Taille de la scene
        // ─────────────────────────────────────────────────────────────
        // La scene est authorée dans un repere fixe (scene.width x scene.height).
        // On calcule la boite qui tient dans la place disponible en conservant le
        // ratio : elle retrecit sur petit ecran et regrandit quand la place
        // revient. Phaser (Scale.FIT) ajuste ensuite le canvas a cette boite.
        const sceneWidth = Number(config?.scene?.width) || 400;
        const sceneHeight = Number(config?.scene?.height) || 400;
        const fitRef = useRef<HTMLDivElement | null>(null);
        const [stageSize, setStageSize] = useState<{ width: number; height: number }>({
            width: sceneWidth,
            height: sceneHeight,
        });

        const computeStageSize = useCallback(() => {
            const container = fitRef.current;
            if (!container) return;

            // Seule la largeur est mesuree : la hauteur du conteneur depend de
            // celle de la scene, la lire ici ferait osciller le calcul.
            const availableWidth = container.clientWidth;
            if (!availableWidth) return;

            const viewportHeight = window.innerHeight || sceneHeight;
            const heightRatio = window.innerWidth < 768 ? 0.42 : 0.62;
            const availableHeight = Math.max(220, viewportHeight * heightRatio);

            const ratio = Math.min(
                availableWidth / sceneWidth,
                availableHeight / sceneHeight,
                // Le canvas garde sa resolution interne : au-dela de x2 l'image
                // devient floue sans rien apporter.
                2
            );
            const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;

            setStageSize((previous) => {
                const width = Math.round(sceneWidth * safeRatio);
                const height = Math.round(sceneHeight * safeRatio);
                if (previous.width === width && previous.height === height) return previous;
                return { width, height };
            });
        }, [sceneWidth, sceneHeight]);

        useEffect(() => {
            computeStageSize();

            const container = fitRef.current;
            const observer =
                typeof ResizeObserver !== "undefined"
                    ? new ResizeObserver(computeStageSize)
                    : null;
            if (container) observer?.observe(container);
            window.addEventListener("resize", computeStageSize);

            return () => {
                observer?.disconnect();
                window.removeEventListener("resize", computeStageSize);
            };
        }, [computeStageSize]);

        // Scale.FIT n'ecoute que le resize de la fenetre : quand c'est la boite
        // qui change (menu lateral, colonne elargie), il faut le lui signaler.
        useEffect(() => {
            engineRef.current?.refreshScale();
        }, [stageSize]);

        function getBootErrorMessage(err: any): string {
            if (!err) return "Unknown Phaser error";
            if (typeof err === "string") return err;
            if (err instanceof Error) return err.message;
            try { return JSON.stringify(err); } catch { return String(err); }
        }
        const [isConfiguratorOpen, setIsConfiguratorOpen] = useState(false);

        const {
            updateScene: updateSceneCtx,
            characters,
            addCharacter: addCharacterCtx,
            addObject: addObjectCtx,
            updateObject: updateObjectCtx,
            removeObject: removeObjectCtx,
            objects,
            texts,
            scene,
            winConditions,
            sounds: configSounds,
            setCharactersForAll,
            setObjectsForAll,
            setTextForAll,
        } = usePhaserConfig();

        const [currentConfig, setCurrentConfig] = useState<PhaserConfig>({
            ...config,
        });

        const [showGrid, setShowGrid] = useState(false);
        const [gridHover, setGridHover] = useState(false);
        useEffect(() => {
            engineRef.current?.setGridEnabled?.(showGrid);
        }, [showGrid]);
        // Update initial reference when a new config is provided (e.g. after import)
        useEffect(() => {
            initialConfigRef.current = config;
        }, [config]);

        // ─────────────────────────────────────────────────────────────
        // Handlers (définis en amont pour éviter les refs non définies)
        // ─────────────────────────────────────────────────────────────
        // Stabilise le reset (utilise la fonction ci-dessus)


        function handleCharacters(engine: GameEngine, list: CharacterConfig[]) {
            engine.handleCharacters(list);
        }

        function handleObjects(engine: GameEngine, list: ObjectConfig[]) {
            engine.handleObjects(list);
        }

        function handleTexts(engine: GameEngine, list: TextObjectConfig[]) {
            engine.handleTexts(list);
        }


        // ─────────────────────────────────────────────────────────────
        // Exposer toutes les fonctions au scope global
        // ─────────────────────────────────────────────────────────────
        useEffect(() => {
            // Tu gardes tes initFunctions(...) comme avant
            functions.initFunctions({
                sprites,
                obstacles,
                addCharacterCtx,
                syncCharactersCtx: (chars: CharacterConfig[]) => {
                    if (engineRef.current?.phaserScene) {
                        handleCharacters(engineRef.current, chars);
                    }
                },
                addObjectCtx,
                updateObjectCtx,
                removeObjectCtx,
                scenes: scenesConfig,
                updateSceneCtx,
            });

            // 👉 expose toutes les fonctions vers l’IDE + compat globale
            functions.exposeIDEGlobals({ alsoOnWindow: true });

            return () => {
                functions.hideIDEGlobals({ alsoFromWindow: true });
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);


        // ⬇️ expose un bridge minimal pour l’IDE
        useEffect(() => {
            const em = eventManager ?? undefined; // on utilise l'EventManager passé par props

            // petit helper pour off() si ton EventManager n’a pas d’API off()
            const safeOff = (name: string, handler: (...a: any[]) => void) => {
                if ((em as any)?.off) {
                    (em as any).off(name, handler);
                } else {
                    const arr = (em as any)?.genericListeners?.get?.(name);
                    if (arr && Array.isArray(arr)) {
                        const i = arr.indexOf(handler);
                        if (i !== -1) arr.splice(i, 1);
                    }
                }
            };

            const apiGlobals = (window as any).__IDE_GLOBALS__;

            (window as any).__IDE_BRIDGE__ = {
                api: apiGlobals, //  permet au shim Python de trouver getDefaultCharacterId(), etc.
                events: {
                    on: (name: string, handler: (...args: any[]) => void) => em?.on?.(name, handler),
                    off: (name: string, handler: (...args: any[]) => void) => safeOff(name, handler),
                    emit: (name: string, ...args: any[]) => em?.emit?.(name, ...args),
                },
            };

            return () => {
                // nettoyage du bridge global quand le composant est démonté
                if ((window as any).__IDE_BRIDGE__) {
                    delete (window as any).__IDE_BRIDGE__;
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [eventManager]);


        // ─────────────────────────────────────────────────────────────
        // Sync characters / objects / texts quand isRunning = true
        // ─────────────────────────────────────────────────────────────
        useEffect(() => {
            if (!isRunning || !engineRef.current?.phaserScene) return;
            handleCharacters(engineRef.current, characters);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [characters, isRunning]);

        useEffect(() => {
            if (!isRunning || !engineRef.current?.phaserScene) return;
            handleObjects(engineRef.current, objects);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [objects, isRunning]);

        useEffect(() => {
            if (!isRunning || !engineRef.current?.phaserScene) return;
            handleTexts(engineRef.current, texts);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [texts, isRunning]);

        useEffect(() => {
            const scene = engineRef.current?.phaserScene;
            scene?.setWinConditions?.(winConditions);
        }, [winConditions, isRunning]);
        // ─────────────────────────────────────────────────────────────
        // Mettre à jour le fond de scène
        // ─────────────────────────────────────────────────────────────
        useEffect(() => {
            if (!isRunning || !engineRef.current?.phaserScene || !scene) return;
            try {
                engineRef.current.updateSceneBackground(scene);
            } catch (err) {
                console.warn("[PhaserComponent] updateSceneBackground skipped:", err);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scene, isRunning]);

        // ─────────────────────────────────────────────────────────────
        // Boot / Stop Phaser Game
        // IMPORTANT: ne pas dépendre des listes runtime (characters/objects/texts)
        // ─────────────────────────────────────────────────────────────
        useEffect(() => {
            if (isRunning) {
                setBootError(null);
                // Guard: if the container is not mounted yet, avoid Phaser mounting into <body>.
                if (!phaserContainerRef.current) {
                    console.warn('[PhaserComponent] phaserContainerRef not ready; skipping boot');
                    return;
                }
                // 1) Construire la DB d’assets IMAGES à partir des catalogues stables
                const assets = buildImageAssets({
                    scenes: scenesConfig,
                    sprites,
                    obstacles,
                    characters,
                    objects,
                });

                // 2) Fusionner les sons: config.sounds (projet) + sounds (bibliothèque)
                const libSoundConfigs = sounds.map((s) => ({
                    adminMode: false,
                    id: String(s.id),
                    name: s.file_name.replace(/\.[^/.]+$/, ""),
                    file_path: s.file_path,
                    volume: 1,
                    loop: false,
                }));

                // 3) Rendre "assets" canonique dans PhaserConfig
                const mergedPhaserConfig: PhaserConfig = {
                    ...config,
                    assets,
                    sounds: mergeSounds(config.sounds, libSoundConfigs),
                    winConditions,
                };

                // 4) Boot du moteur (on continue aussi à passer 'assets' pour compat)

                let engine: GameEngine | null = null;
                try {
                    engine = new GameEngine(
                        adminMode,
                        {
                            eventManager,
                            PhaserConfig: mergedPhaserConfig,
                            assets, // ← back-compat si la scène le lit encore depuis 'options'
                        },
                        phaserContainerRef,
                        onWin
                    );
                    engineRef.current = engine;
                    functions.setEngine(engine);
                    engine.start();

                    const t = window.setTimeout(() => {
                        const el = phaserContainerRef.current;
                        const hasCanvas = !!el?.querySelector?.("canvas");
                        if (!hasCanvas) {
                            setBootError(
                                "Phaser n'a pas créé de canvas. Souvent: WebGL désactivé / navigateur trop ancien."
                            );
                        }
                    }, 1500);
                    (engine as any).__bootTimer = t;
                } catch (err) {
                    console.error("[PhaserComponent] Engine boot failed:", err);
                    setBootError(getBootErrorMessage(err));
                    try { engine?.stop(); } catch { }
                    engineRef.current = null;
                    functions.setEngine(null as any);
                    return;
                }

                engineRef.current = engine;
                functions.setEngine(engine);
                engine.start();

                return () => {
                    const timer = (engine as any)?.__bootTimer;
                    if (timer) window.clearTimeout(timer);
                    engine?.stop();
                    engineRef.current = null;
                };
            } else {
                engineRef.current?.stop();
                engineRef.current = null;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            isRunning,
            scenesConfig,
            sprites,
            obstacles,
            sounds,
            engineNonce,
        ]);


        // ─────────────────────────────────────────────────────────────
        // Maintenir la "currentConfig" pour l’API getConfig()
        // ─────────────────────────────────────────────────────────────
        useEffect(() => {
            setCurrentConfig((prev) => ({
                ...prev,
                workspace: config.workspace,
                scene: scene,
                characters: characters,
                objects: objects,
                texts: texts,
                winConditions: winConditions,
                sounds: configSounds,
            }));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [config, scene, characters, objects, texts, winConditions, configSounds]);



        const loadConfigIntoContext = useCallback(
            (cfg: PhaserConfig) => {
                updateSceneCtx(cfg.scene);
                setCharactersForAll(cfg.characters ?? []);
                setObjectsForAll(cfg.objects ?? []);
                setTextForAll(cfg.texts ?? []);
                // Si tu ajoutes plus tard setWinConditions/setSounds, mémorise-les ici aussi
            },
            [updateSceneCtx, setCharactersForAll, setObjectsForAll, setTextForAll]
        );

        const handleResetConfig = useCallback(async () => {
            try {
                engineRef.current?.stop();
            } catch { }
            engineRef.current = null;

            console.log("reset")
            const initial = initialConfigRef.current;
            loadConfigIntoContext(initial);
            setCurrentConfig({ ...initial });
            setEngineNonce((n) => n + 1);
        }, [loadConfigIntoContext]);

        useEffect(() => {
            const em = eventManager;
            if (!em) return;

            const onStop = () => {
                // reset la scène quand l’IDE stoppe
                handleResetConfig();
            };

            em.on?.('ide:stop', onStop);
            return () => {
                if (em?.off) em.off('ide:stop', onStop);
                else {
                    const arr = (em as any)?.genericListeners?.get?.('ide:stop');
                    if (arr && Array.isArray(arr)) {
                        const i = arr.indexOf(onStop);
                        if (i !== -1) arr.splice(i, 1);
                    }
                }
            };
        }, [eventManager, handleResetConfig]);

        // Exposer l’API imperative
        useImperativeHandle(
            ref,
            () => ({
                getConfig: () => currentConfig,
                handleResetConfig,
            }),
            [currentConfig, handleResetConfig]
        );
        // ─────────────────────────────────────────────────────────────
        // Render
        // ─────────────────────────────────────────────────────────────
        return (
            <div
                ref={fitRef}
                style={{
                    width: "100%",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                }}
            >
            <div
                style={{
                    position: "relative", // <— pour positionner le bouton
                    // Taille calculee a partir de la place reellement disponible :
                    // la scene retrecit ET regrandit, en gardant son ratio.
                    width: stageSize.width,
                    height: stageSize.height,
                    flex: "0 0 auto",
                }}
            >
                {bootError && (
                    <div style={{
                        position: "absolute", inset: 0, zIndex: 50,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 12, background: "rgba(255,255,255,0.92)",
                        color: "#a8071a", textAlign: "center"
                    }}>
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                Phaser ne démarre pas sur cet ordinateur.
                            </div>
                            <div style={{ marginBottom: 6 }}>{bootError}</div>
                            <div style={{ color: "rgba(0,0,0,0.6)" }}>
                                Conseil: mettre à jour le navigateur et activer l'accélération matérielle / WebGL.
                            </div>
                        </div>
                    </div>
                )}
                <div
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 20,
                    }}
                    onMouseEnter={() => setGridHover(true)}
                    onMouseLeave={() => setGridHover(false)}
                >
                    <div
                        style={{
                            height: 32,
                            minWidth: 32,
                            width: "fit-content",
                            maxWidth: gridHover ? 220 : 32,
                            overflow: "hidden",
                            transition:
                                "max-width 160ms ease, background 160ms ease, box-shadow 160ms ease, padding 160ms ease",

                            borderRadius: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: gridHover ? "flex-start" : "center",
                            cursor: "pointer",
                            userSelect: "none",

                            padding: gridHover ? "0 8px" : "0",
                            gap: 6,

                            background: showGrid
                                ? "rgba(24,144,255,0.85)"
                                : "rgba(255,255,255,0.85)",
                            color: showGrid ? "#fff" : "#333",
                            border: "1px solid rgba(0,0,0,0.08)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                        onClick={() => setShowGrid((v) => !v)}
                        role="button"
                        aria-label="Afficher la grille"
                    >
                        {/* Symbole toujours visible */}
                        <div
                            style={{
                                width: 16,
                                height: 16,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                lineHeight: 1,
                                flex: "0 0 auto",
                                color: showGrid ? "#fff" : "#555",
                            }}
                        >
                            #
                        </div>

                        {/* Zone étendue : inexistante quand fermé */}
                        {gridHover && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    whiteSpace: "nowrap",
                                    fontSize: 12,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <span style={{ lineHeight: 1 }}>
                                    Grille
                                </span>

                                <Checkbox
                                    checked={showGrid}
                                    style={{ margin: 0 }}
                                    onChange={(e) => setShowGrid(e.target.checked)}
                                />
                            </div>
                        )}
                    </div>
                </div>



                <div
                    key={engineNonce}
                    ref={phaserContainerRef}
                    style={{
                        // Phaser (Scale.FIT) adapte le canvas a cette boite.
                        width: "100%",
                        height: "100%",
                    }}
                />

                {adminMode && (
                    <>
                        {/* Bouton normal en dessous */}
                        <div style={{ textAlign: "center", marginTop: 12 }}>
                            <Button
                                type="default"
                                onClick={() => setIsConfiguratorOpen(true)}
                            >
                                Ouvrir le configurateur
                            </Button>
                        </div>

                        {/* Drawer latéral gauche */}
                        <Drawer
                            title="Configuration de la scène"
                            placement="left"
                            open={isConfiguratorOpen}
                            onClose={() => setIsConfiguratorOpen(false)}
                            width="min(720px, 100vw - 24px)"
                            mask={false}             // laisse la scène interactive
                            destroyOnClose={false}   // garde l'état des onglets
                            styles={{
                                // header géré par antd ; on force le body à occuper 100% et en flex-col
                                body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 },
                            }}
                        >
                            <ConfiguratorPanel
                                sprites={sprites}
                                scenesConfig={scenesConfig}
                                obstacles={obstacles}
                                textsConfig={textsConfig}
                                winConditions={winConditions}
                                sounds={config.sounds}
                            />
                        </Drawer>
                    </>
                )}

            </div>
            </div>
        );
    }
);

PhaserComponent.displayName = "PhaserComponent";

// ─────────────────────────────────────────────────────────────
// Provider wrapper
// ─────────────────────────────────────────────────────────────
const PhaserView = forwardRef<PhaserRef, PhaserViewProps>((props, ref) => {
    return (
        <PhaserConfigProvider initialConfig={props.config}>
            <PhaserComponent ref={ref} {...props} />
        </PhaserConfigProvider>
    );
});

export default PhaserView;
